<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import InboxesAPI from 'dashboard/api/inboxes';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');
const enablingId = ref(null);
const {
  isCloudFeatureEnabled,
  isOnChatipsCloud,
  isMetaInboxCreationDisabled,
} = useAccount();

const hasWhatsappAppId = computed(
  () =>
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
);

const showEmbeddedSignup = computed(
  () =>
    hasWhatsappAppId.value &&
    !isMetaInboxCreationDisabled.value &&
    (!isOnChatipsCloud.value ||
      isCloudFeatureEnabled(FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP_FLOW))
);

const cloudInboxes = computed(() =>
  (inboxes.value || []).filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);

onMounted(() => {
  store.dispatch('inboxes/get');
});

const openCallingSettings = inboxId => {
  router.replace({
    name: 'settings_inbox_show',
    params: { inboxId, tab: 'calls-configuration' },
  });
};

const enableExisting = async inbox => {
  enablingId.value = inbox.id;
  try {
    await InboxesAPI.enableWhatsappCalling(inbox.id);
    openCallingSettings(inbox.id);
  } catch (_) {
    useAlert(t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE_FAILED'));
  } finally {
    enablingId.value = null;
  }
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div class="px-6 py-5 rounded-2xl border border-n-weak">
      <h2 class="mb-1 text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_CALLING.EXISTING.TITLE') }}
      </h2>
      <p class="mb-4 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_CALLING.EXISTING.DESCRIPTION') }}
      </p>
      <p v-if="!cloudInboxes.length" class="mb-6 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_CALLING.EXISTING.EMPTY') }}
      </p>
      <ul v-else class="flex flex-col gap-3 mb-6">
        <li
          v-for="inbox in cloudInboxes"
          :key="inbox.id"
          class="flex items-center justify-between gap-3"
        >
          <span class="text-sm text-n-slate-12">
            {{ inbox.name }}
            <span v-if="inbox.phone_number" class="text-n-slate-11">
              {{ inbox.phone_number }}
            </span>
          </span>
          <NextButton
            sm
            solid
            blue
            type="button"
            :label="$t('INBOX_MGMT.WHATSAPP_CALLING.EXISTING.ENABLE')"
            :is-loading="enablingId === inbox.id"
            :disabled="enablingId !== null"
            @click="enableExisting(inbox)"
          />
        </li>
      </ul>

      <h2 class="mb-4 text-base font-medium text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_CALLING.NEW.TITLE') }}
      </h2>
      <WhatsappEmbeddedSignup
        v-if="showEmbeddedSignup"
        class="mb-6"
        enable-calling-on-complete
      />
      <CloudWhatsapp enable-calling-on-complete />
    </div>
  </div>
</template>
